import { TrainMode } from '@jaaluu/interfaces/TrainMode';
import { CourseService, Language, LanguageLevel, Story } from '@shared/openapi/jaaluu-api';
import { ref, Ref, watch } from 'vue';
import { language } from '.';

const SMALL_COURSE_IMAGES = [
  'boy.png',
  'boy.gif',
  'man.png',
  'man.gif',
  'woman.png',
  'woman.gif',
  'girl.png',
  'girl.gif',
  'family.png',
  'family.gif',
  'children.png',
  'children.gif',
  'family_with_names.png',
  'family_with_names.gif',
  'man_and_woman.png',
  'man_and_woman.gif',
  'woman_kids.png',
  'woman_kids.gif'
];

export type TrainType =
  | 'flipcards'
  | 'vocabularies'
  | 'exercises'
  | 'repeat'
  | 'sentences_only'
  | 'gap_text'
  | 'phrases'
  | 'sentence_matcher'
  | 'def_matcher'
  | 'train_questions';

export interface CourseStatus {
  langForeign: Language;
  langNative: Language;
  storyIdx: number;
  trainIdx: number;
  sentenceIdx: number;
  currentWordIdx: number;
  isActive: boolean;
  updateIsActive: boolean;
  backFromRepeatFavs: boolean;
}

export interface TrainStep {
  type: TrainType;
  params: Object;
}

export interface StructureStory {
  storyIdx: number;
  title: string;
  imageName: string;
  trainSteps: TrainStep[];
}

// LocalStorage key for course status
const COURSE_STATUS_STORAGE_KEY = 'courseStatus';
const DEFAULT_COURSE_STATE = {
  langForeign: Language.EN,
  langNative: Language.DE,
  trainIdx: -1,
  isActive: false,
  storyIdx: 0,
  sentenceIdx: 0,
  currentWordIdx: 0,
  updateIsActive: false,
  backFromRepeatFavs: false
};

export default () => {
  const { langForeign, langNative, langLevel } = language.state;
  const stories: Ref<Story[]> = ref([]);
  const courseStatusLangs: Ref<Record<string, CourseStatus>> = ref({});
  const curriculum = ref<StructureStory[]>([]);

  const getLangKey = () => {
    return `${langForeign.value}_${langNative.value}`;
  };
  // Save course status to localStorage
  const saveCourseStatusToLocalStorage = (status: CourseStatus) => {
    try {
      if (!langForeign.value || !langNative.value) {
        console.warn('Language settings are not defined. Cannot save course status.');
        return;
      }
      status.langForeign = langForeign.value!;
      status.langNative = langNative.value!;
      courseStatusLangs.value[getLangKey()] = status;
      localStorage.setItem(COURSE_STATUS_STORAGE_KEY, JSON.stringify(courseStatusLangs.value));
    } catch (error) {
      console.error('Failed to save course status to localStorage:', error);
    }
  };
  // Load initial course status from localStorage or use defaults
  const loadCourseStatusFromLocalStorage = () => {
    if (langForeign.value && langNative.value) {
      const stored = localStorage.getItem(COURSE_STATUS_STORAGE_KEY);
      if (stored && langForeign.value && langNative.value) {
        courseStatusLangs.value = JSON.parse(stored);
        if (Object.keys(courseStatusLangs.value).includes(getLangKey())) {
          courseStatus.value = courseStatusLangs.value[getLangKey()];
        } else {
          courseStatus.value = JSON.parse(JSON.stringify(DEFAULT_COURSE_STATE));
        }
      } else {
        courseStatus.value.langForeign = langForeign.value;
        courseStatus.value.langNative = langNative.value;
      }
    } else {
      console.warn('Language settings are not defined. Cannot load course status.');
    }
    courseStatus.value.updateIsActive = false;
  };

  const courseStatus: Ref<CourseStatus> = ref(JSON.parse(JSON.stringify(DEFAULT_COURSE_STATE)));
  const currentImg: Ref<{
    url: string | null | undefined;
    isBig: boolean;
  }> = ref({ url: null, isBig: false });
  const showChapterDone = ref(false);
  const completedChapterNumber = ref('');

  // Watch for changes in courseStatus and save to localStorage
  watch(
    courseStatus,
    (newStatus) => {
      saveCourseStatusToLocalStorage(newStatus);
    },
    { deep: true }
  );

  const setCourseStatus = (status: CourseStatus) => {
    courseStatus.value = status;
  };

  const loadStories = async () => {
    return CourseService.getShortStoriesV1CourseStoriesGet({
      query: {
        language_level: langLevel.value!,
        language: langForeign.value!
      }
    }).then((res) => {
      if (!res.data) return;
      stories.value = res.data;
      generateCourseCurriculum();
    });
  };

  // Generate course structure
  const generateCourseCurriculum = () => {
    console.debug('Generating course structure');
    const _curr: StructureStory[] = [];
    stories.value.forEach((story, storyIdx) => {
      const _steps = {
        storyIdx,
        title: story.title,
        imageName: story.title_image,
        trainSteps: [
          {
            type: 'sentences_only',
            params: {
              showSentencesOnly: true
            }
          },
          {
            type: 'vocabularies',
            params: {}
          },
          {
            type: 'flipcards',
            params: {
              storyIdx
            }
          },
          {
            type: 'exercises',
            params: {
              storyIdx,
              trainMode: TrainMode.BY_WORD,
              favGroup: 'course'
            }
          },
          {
            type: 'vocabularies',
            params: {
              nativeToForeign: true,
              showTranslationAuto: false
            }
          },
          {
            type: 'sentences_only',
            params: {
              showSentencesOnly: true,
              nativeToForeign: true,
              showTranslationAuto: false
            }
          },
          {
            type: 'exercises',
            params: {
              storyIdx,
              trainMode: TrainMode.BY_WORD,
              favGroup: 'course'
            }
          },
          {
            type: 'sentences_only',
            params: {
              showSentencesOnly: true,
              source: 'train_sentences',
              showTranslationAuto: false
            }
          },
          {
            type: 'gap_text',
            params: {}
          }
        ]
      };
      if (langLevel.value !== LanguageLevel.A1_A2) {
        _steps.trainSteps.push({
          type: 'train_questions',
          params: {}
        });
      }
      _curr.push(_steps);
    });
    curriculum.value = _curr;
  };

  const getTrainUrl = (trainType: TrainType, storyIdx: number) => {
    if (trainType === 'sentences_only') {
      return `/${langForeign.value}/course/story/${storyIdx}`;
    } else if (trainType === 'vocabularies') {
      return `/${langForeign.value}/course/vocabularies/${storyIdx}`;
    } else if (trainType === 'flipcards') {
      return `/${langForeign.value}/learn/flipcards`;
    } else if (trainType === 'exercises') {
      return `/${langForeign.value}/learn/exercises`;
    } else if (trainType === 'train_questions') {
      return `/${langForeign.value}/learn/train-questions`;
    } else if (trainType === 'gap_text') {
      return `/${langForeign.value}/learn/gap-text/${storyIdx}`;
    }
    return 'xxx';
  };

  const getStoryUrl = (storyIdx: number) => {
    return `/${langForeign.value}/course/story/${storyIdx}`;
  };

  const getParamsStr = (params: any) => {
    console.log('params', params);
    let paramsStr = '?';
    Object.keys(params).forEach((k, idx) => {
      if (idx > 0) {
        paramsStr += '&';
      }
      paramsStr += `${k}=${params[k]}`;
    });
    return paramsStr;
  };

  return {
    state: {
      courseStatus,
      currentImg,
      stories,
      curriculum,
      showChapterDone,
      completedChapterNumber
    },
    funcs: {
      setCourseStatus,
      generateCourseCurriculum,
      loadStories,
      getStoryUrl,
      getParamsStr,
      getTrainUrl,
      loadCourseStatusFromLocalStorage
    },
    consts: {
      SMALL_COURSE_IMAGES
    }
  };
};
